import { useAtomValue } from "jotai";
import Image from "next/image";
import { useCallback, useMemo } from "react";

import { CardCategoryEnum } from "@sorcery/db";
import { useBreakpoints } from "@sorcery/ui";

import { CardRemover } from "~/components/molecules/card/CardRemover";
import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { useSampleHand } from "~/providers/SampleHandProvider";
import { useUser } from "~/providers/UserProvider";
import { deckJotai } from "~/state/deckJotai";
import type { DeckCardType } from "~/types/deck";
import { DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH } from "~/utils/card";

export const HandCard = ({
  deckCard,
  index,
  handLength,
}: {
  deckCard: DeckCardType;
  index: number;
  handLength: number;
}) => {
  const { openModal } = useModal();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  const { discardCard } = useSampleHand();
  const { user } = useUser();
  const { isXl, isLg, isMd, isSm } = useBreakpoints();

  const isAuthor = useMemo(() => user && deck.user && deck.user.id === user.id, [deck.user, user]);

  const isSite = useMemo(
    () => deckCard.card.guardian?.category === CardCategoryEnum.Site,
    [deckCard],
  );

  const childWidth = useMemo(() => {
    let w = 100;
    if (isSm) w = 125.4;
    if (isMd) w = 160;
    if (isLg) w = 200;
    if (isXl) w = 254.6;
    return w;
  }, [isLg, isMd, isSm, isXl]);

  const delta = useMemo(
    () => `calc(
    (${handLength} * ${childWidth}px) - 100%
  )`,
    [handLength, childWidth],
  );

  const offset = useMemo(() => `calc(${delta} / (${handLength} - 1))`, [delta, handLength]);

  const left = useMemo(
    () => (index === 0 ? 0 : `calc(${index} * (${childWidth}px - ${offset}))`),
    [index, childWidth, offset],
  );

  const getRotation = useCallback((totalCards: number, cardIndex: number) => {
    const baseRotation = 7; // You can adjust this value to get the desired effect.
    const half = Math.floor(totalCards / 2);

    // If the total number of cards is even, we need to adjust the pivot slightly.
    const isEven = totalCards % 2 === 0;
    const pivot = isEven ? half - 0.5 : half;

    const distanceFromPivot = cardIndex - pivot;
    const rotation = baseRotation * distanceFromPivot;

    return rotation;
  }, []);

  const rounding = useMemo(() => {
    return isXl ? "rounded-[18px]" : isLg ? "rounded-[16px]" : "rounded-[12px]";
  }, [isXl, isLg]);

  const rotation = useMemo(() => {
    const r = getRotation(handLength, index);
    return Math.sign(r) === 1 ? Math.min(r, 30) : Math.max(r, -30);
  }, [getRotation, handLength, index]);

  const style: React.CSSProperties = useMemo(() => {
    return isSite
      ? {
          transform: "rotate(90deg)",
        }
      : {};
  }, [isSite]);

  return (
    <div
      key={index}
      style={{
        // zIndex: handLength - index,
        left: left,
        top: 0,
        width: childWidth,
        transform: `perspective(1000px) rotateZ(${rotation}deg)`,
        // paddingTop: getTopOffset(handLength, index),
        // marginTop: -(handLength * 4),
      }}
      // group class handles discard button hover state
      className={`group ${index === 0 ? "relative" : "absolute"} duration-300 animate-in fade-in`}
    >
      <Image
        className={`${rounding} cursor-pointer shadow-[0_0_8px_rgba(0,0,0,0.5)] outline outline-1 outline-transparent transition-all 
        hover:shadow-[0_0_14px_rgba(255,255,255,0.75)] hover:outline-white`}
        height={DEFAULT_CARD_HEIGHT}
        width={DEFAULT_CARD_WIDTH}
        alt={deckCard.card.name}
        src={deckCard.variant.src}
        onClick={() => {
          isAuthor
            ? openModal(ModalTypeEnum.DECKGALLERY, {
                card: deckCard.card,
              })
            : openModal(ModalTypeEnum.CARDGALLERY, { card: deckCard.card });
        }}
        style={style}
      />
      {/* remover */}
      {isSm && <CardRemover offset={isSite} onRemove={() => discardCard(index)} />}
    </div>
  );
};
